(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initialized = {
    buttons: new WeakSet(),
    galleries: new WeakSet(),
    renders: new WeakSet(),
    reveals: new WeakSet(),
    tilts: new WeakSet(),
    spotlights: new WeakSet(),
    coinScenes: new WeakSet(),
    rewardTabs: new WeakSet(),
    battleRails: new WeakSet(),
  };
  const renderStates = new WeakMap();
  let houseExperienceReady = false;
  let cartCelebrationTimer = 0;
  let checkoutTransitionTimer = 0;
  let clickTransitionTimer = 0;
  let navigationPending = false;
  let lastAddContext = null;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const smoothstep = (edge0, edge1, value) => {
    const amount = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return amount * amount * (3 - 2 * amount);
  };

  const hexToRgb = (hex) => {
    const clean = String(hex || '').replace('#', '').trim();
    if (!/^[0-9a-f]{6}$/i.test(clean)) return { r: 122, g: 117, b: 110 };
    return {
      r: Number.parseInt(clean.slice(0, 2), 16),
      g: Number.parseInt(clean.slice(2, 4), 16),
      b: Number.parseInt(clean.slice(4, 6), 16),
    };
  };

  const luminance = (r, g, b) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  const pointInPolygon = (x, y, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi || 1) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  };

  const garmentPolygons = (square, kind) => {
    if (square) {
      return [
        [[0, 0.50], [0.08, 0.23], [0.44, 0.20], [0.57, 0.40], [0.56, 0.93], [0, 0.93]],
        [[0.44, 0.40], [0.56, 0.20], [0.92, 0.23], [1, 0.50], [1, 0.93], [0.44, 0.93]],
      ];
    }

    return [
      [[0, 0.38], [0.06, 0.055], [0.44, 0.035], [0.55, 0.25], [0.55, 0.96], [0, 0.96]],
      [[0.45, 0.25], [0.56, 0.035], [0.94, 0.055], [1, 0.38], [1, 0.96], [0.45, 0.96]],
    ];
  };

  const detectFabricColor = (imageData, width, height, square) => {
    const data = imageData.data;
    const x0 = Math.floor(width * (square ? 0.16 : 0.15));
    const x1 = Math.floor(width * 0.43);
    const y0 = Math.floor(height * (square ? 0.52 : 0.46));
    const y1 = Math.floor(height * (square ? 0.79 : 0.82));
    const bins = new Map();
    const stride = Math.max(1, Math.floor(width / 520));

    for (let y = y0; y < y1; y += stride) {
      for (let x = x0; x < x1; x += stride) {
        const offset = (y * width + x) * 4;
        if (data[offset + 3] < 245) continue;
        const r = data[offset];
        const g = data[offset + 1];
        const b = data[offset + 2];
        const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
        const bin = bins.get(key) || { count: 0, r: 0, g: 0, b: 0 };
        bin.count += 1;
        bin.r += r;
        bin.g += g;
        bin.b += b;
        bins.set(key, bin);
      }
    }

    let winner = null;
    bins.forEach((bin) => {
      if (!winner || bin.count > winner.count) winner = bin;
    });

    if (!winner) return { r: 80, g: 80, b: 80 };
    return {
      r: winner.r / winner.count,
      g: winner.g / winner.count,
      b: winner.b / winner.count,
    };
  };

  const detectBackgroundColors = (source, width, height, polygons) => {
    const pixels = source.data;
    const bins = new Map();
    const stride = Math.max(2, Math.floor(width / 420));
    for (let y = 0; y < height; y += stride) {
      const ny = y / height;
      for (let x = 0; x < width; x += stride) {
        const nx = x / width;
        if (pointInPolygon(nx, ny, polygons[0]) || pointInPolygon(nx, ny, polygons[1])) continue;
        const offset = (y * width + x) * 4;
        const r = pixels[offset];
        const g = pixels[offset + 1];
        const b = pixels[offset + 2];
        const key = `${r >> 4}-${g >> 4}-${b >> 4}`;
        const bin = bins.get(key) || { count: 0, r: 0, g: 0, b: 0 };
        bin.count += 1;
        bin.r += r;
        bin.g += g;
        bin.b += b;
        bins.set(key, bin);
      }
    }

    const ranked = [...bins.values()].sort((a, b) => b.count - a.count);
    const colors = [];
    for (const bin of ranked) {
      const color = { r: bin.r / bin.count, g: bin.g / bin.count, b: bin.b / bin.count };
      const distinct = colors.every((existing) => Math.hypot(
        color.r - existing.r,
        color.g - existing.g,
        color.b - existing.b,
      ) > 14);
      if (distinct) colors.push(color);
      if (colors.length === 12) break;
    }
    return colors;
  };

  const buildConfidenceMask = (source, width, height, base, silhouette) => {
    const pixels = source.data;
    const mask = new Uint8Array(width * height);
    const baseLum = luminance(base.r, base.g, base.b);
    const baseChromaA = (base.r - base.g) / 255;
    const baseChromaB = (base.b - base.g) / 255;
    const baseSpread = (Math.max(base.r, base.g, base.b) - Math.min(base.r, base.g, base.b)) / 255;
    const neutralBase = baseSpread < 0.14;
    const chromaSoft = neutralBase ? 0.045 : 0.045;
    const chromaHard = neutralBase ? 0.16 : 0.185;
    const lumaSoft = baseLum < 0.22 ? 0.16 : 0.22;
    const lumaHard = baseLum < 0.22 ? 0.38 : baseLum > 0.76 ? 0.48 : 0.43;

    for (let y = 0; y < height; y += 1) {
      const ny = y / height;
      for (let x = 0; x < width; x += 1) {
        const pixelIndex = y * width + x;
        const silhouetteValue = silhouette[pixelIndex] / 255;
        if (silhouetteValue < 0.04) continue;
          const offset = pixelIndex * 4;
          const r = pixels[offset];
          const g = pixels[offset + 1];
          const b = pixels[offset + 2];
          const pixelLum = luminance(r, g, b);
          const chromaA = (r - g) / 255;
          const chromaB = (b - g) / 255;
          const chromaDistance = Math.hypot(chromaA - baseChromaA, chromaB - baseChromaB);
          const lumaDistance = Math.abs(pixelLum - baseLum);
          const nx = x / width;
          const artZone = (nx > 0.29 && nx < 0.45 && ny > 0.16 && ny < 0.40)
            || (nx > 0.55 && nx < 0.94 && ny > 0.12 && ny < 0.80);
          const pixelChromaSoft = artZone ? chromaSoft : neutralBase ? 0.065 : 0.075;
          const pixelChromaHard = artZone ? chromaHard : neutralBase ? 0.22 : 0.30;
          const pixelLumaSoft = artZone ? lumaSoft : Math.min(lumaSoft + 0.05, 0.3);
          const pixelLumaHard = artZone ? lumaHard : Math.min(lumaHard + 0.1, 0.56);
          const chromaScore = 1 - smoothstep(pixelChromaSoft, pixelChromaHard, chromaDistance);
          const lumaScore = 1 - smoothstep(pixelLumaSoft, pixelLumaHard, lumaDistance);
          const confidence = chromaScore * lumaScore;
          if (confidence >= 0.08) {
            mask[pixelIndex] = Math.round(confidence * silhouetteValue * 255);
          }
      }
    }

    return mask;
  };

  const prepareRenderer = async (render) => {
    const existing = renderStates.get(render);
    if (existing?.source) return existing;
    if (existing?.promise) return existing.promise;

    const image = render.querySelector('[data-buffalo-render-source]');
    const maskImage = render.querySelector('[data-buffalo-render-mask]');
    const canvas = render.querySelector('[data-buffalo-render-canvas]');
    if (!image || !maskImage || !canvas) throw new Error('Buffalo color renderer is missing a locked source or silhouette.');

    const state = existing || { job: 0 };
    const promise = (async () => {
      const waitForImage = (element) => {
        if (element.complete && element.naturalWidth) return Promise.resolve();
        return new Promise((resolve, reject) => {
          element.addEventListener('load', resolve, { once: true });
          element.addEventListener('error', reject, { once: true });
        });
      };
      await Promise.all([waitForImage(image), waitForImage(maskImage)]);

      const stage = Boolean(render.closest('[data-buffalo-color-stage]'));
      const cap = stage ? 1200 : 480;
      const scale = Math.min(1, cap / image.naturalWidth);
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
      context.drawImage(image, 0, 0, width, height);
      const source = context.getImageData(0, 0, width, height);
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskContext = maskCanvas.getContext('2d', { alpha: false, willReadFrequently: true });
      maskContext.drawImage(maskImage, 0, 0, width, height);
      const maskPixels = maskContext.getImageData(0, 0, width, height).data;
      const silhouette = new Uint8Array(width * height);
      for (let pixelIndex = 0; pixelIndex < silhouette.length; pixelIndex += 1) {
        silhouette[pixelIndex] = maskPixels[pixelIndex * 4];
      }
      const square = width / height < 1.18;
      state.source = source;
      state.context = context;
      state.width = width;
      state.height = height;
      state.square = square;
      state.base = detectFabricColor(source, width, height, square);
      state.confidence = buildConfidenceMask(source, width, height, state.base, silhouette);
      state.promise = null;
      renderStates.set(render, state);
      return state;
    })();

    state.promise = promise;
    renderStates.set(render, state);
    return promise;
  };

  const renderGarmentColor = async (render, color) => {
    if (!render || !color) return;
    const requestedJob = (renderStates.get(render)?.job || 0) + 1;
    const pending = renderStates.get(render) || {};
    pending.job = requestedJob;
    renderStates.set(render, pending);

    try {
      const state = await prepareRenderer(render);
      if (state.job !== requestedJob) return;

      const target = hexToRgb(color);
      const base = state.base;
      const baseLum = luminance(base.r, base.g, base.b);
      const targetLum = luminance(target.r, target.g, target.b);
      const textureScale = targetLum < 0.12 ? 0.34 : targetLum > 0.82 ? 0.72 : 0.58;
      const pixels = new Uint8ClampedArray(state.source.data);
      const { width, height, confidence } = state;

      for (let pixelIndex = 0; pixelIndex < confidence.length; pixelIndex += 1) {
        const confidenceValue = confidence[pixelIndex] / 255;
        if (confidenceValue < 0.08) continue;
        const offset = pixelIndex * 4;
        const r = pixels[offset];
        const g = pixels[offset + 1];
        const b = pixels[offset + 2];
        const pixelLum = luminance(r, g, b);

        const texture = (pixelLum - baseLum) * 255 * textureScale;
        const targetR = clamp(target.r + texture, 2, 253);
        const targetG = clamp(target.g + texture, 2, 253);
        const targetB = clamp(target.b + texture, 2, 253);
        const blend = 0.94 * confidenceValue;
        pixels[offset] = r + (targetR - r) * blend;
        pixels[offset + 1] = g + (targetG - g) * blend;
        pixels[offset + 2] = b + (targetB - b) * blend;
      }

      if (state.job !== requestedJob) return;
      state.context.putImageData(new ImageData(pixels, width, height), 0, 0);
      render.dataset.buffaloRenderColor = color;
      render.classList.add('is-processed');
    } catch (error) {
      render.classList.add('is-fallback');
    }
  };

  const scheduleRender = (render, immediate = false) => {
    if (!render || initialized.renders.has(render)) return;
    initialized.renders.add(render);
    const run = () => renderGarmentColor(render, render.dataset.buffaloRenderColor);
    if (immediate || !('requestIdleCallback' in window)) run();
    else window.requestIdleCallback(run, { timeout: 1800 });
  };

  const initReveals = (root) => {
    const items = [...root.querySelectorAll('[data-buffalo-reveal]')].filter((item) => !initialized.reveals.has(item));
    items.forEach((item, index) => {
      initialized.reveals.add(item);
      item.style.setProperty('--buffalo-reveal-delay', `${Math.min(index % 8, 6) * 65}ms`);
    });
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const initColorGallery = (gallery) => {
    if (initialized.galleries.has(gallery)) return;
    initialized.galleries.add(gallery);
    const stage = document.querySelector('[data-buffalo-color-stage]');
    const stageRender = stage?.querySelector('[data-buffalo-color-render]');
    const stageLabel = stage?.querySelector('[data-buffalo-stage-label]');
    const cards = [...gallery.querySelectorAll('[data-buffalo-color-choice]')];
    const rail = gallery.querySelector('[data-buffalo-color-rail]');

    if (stageRender) scheduleRender(stageRender, true);
    cards.forEach((card) => scheduleRender(card.querySelector('[data-buffalo-color-render]')));

    const preview = (card) => {
      if (!stage || !stageRender || !card) return;
      const color = card.dataset.buffaloColorValue;
      const label = card.dataset.buffaloColorLabel;
      stage.style.setProperty('--buffalo-color', color);
      stage.classList.add('is-previewing');
      if (stageLabel) stageLabel.textContent = label;
      renderGarmentColor(stageRender, color);
    };

    const restore = () => {
      if (!stage || !stageRender) return;
      const color = stage.dataset.buffaloDefaultColor;
      stage.style.setProperty('--buffalo-color', color);
      stage.classList.remove('is-previewing');
      if (stageLabel) stageLabel.textContent = stage.dataset.buffaloDefaultLabel;
      renderGarmentColor(stageRender, color);
    };

    cards.forEach((card) => {
      card.addEventListener('mouseenter', () => preview(card));
      card.addEventListener('mouseleave', restore);
      card.addEventListener('focus', () => preview(card));
      card.addEventListener('blur', restore);
    });

    if (rail && finePointer.matches) {
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      rail.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        moved = false;
        rail.setPointerCapture(event.pointerId);
        rail.classList.add('is-dragging');
      });
      rail.addEventListener('pointermove', (event) => {
        if (!rail.hasPointerCapture(event.pointerId)) return;
        const distance = event.clientX - startX;
        moved ||= Math.abs(distance) > 6;
        rail.scrollLeft = startScroll - distance;
      });
      rail.addEventListener('pointerup', (event) => {
        if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        rail.classList.remove('is-dragging');
        if (!moved) return;
        rail.addEventListener('click', (clickEvent) => clickEvent.preventDefault(), { capture: true, once: true });
      });
      rail.addEventListener('pointercancel', () => rail.classList.remove('is-dragging'));
    }
  };

  const initLuxuryRails = (root) => {
    root.querySelectorAll('[data-buffalo-battle-rail], [data-buffalo-scroll-rail]').forEach((rail) => {
      if (initialized.battleRails.has(rail)) return;
      initialized.battleRails.add(rail);

      rail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        rail.scrollBy({
          left: direction * Math.max(260, rail.clientWidth * 0.76),
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
      });

      if (!finePointer.matches) return;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      const finishDrag = (event) => {
        if (event?.pointerId !== undefined && rail.hasPointerCapture(event.pointerId)) {
          rail.releasePointerCapture(event.pointerId);
        }
        rail.classList.remove('is-dragging');
        if (!moved) return;
        rail.addEventListener('click', (clickEvent) => clickEvent.preventDefault(), { capture: true, once: true });
      };

      rail.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || rail.scrollWidth <= rail.clientWidth + 2) return;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        moved = false;
        rail.setPointerCapture(event.pointerId);
        rail.classList.add('is-dragging');
      });
      rail.addEventListener('pointermove', (event) => {
        if (!rail.hasPointerCapture(event.pointerId)) return;
        const distance = event.clientX - startX;
        moved ||= Math.abs(distance) > 6;
        rail.scrollLeft = startScroll - distance;
      });
      rail.addEventListener('pointerup', finishDrag);
      rail.addEventListener('pointercancel', finishDrag);
    });
  };

  const initTilt = (root) => {
    if (!finePointer.matches || reduceMotion.matches) return;
    root.querySelectorAll('[data-buffalo-tilt], .buffalo-edit__card').forEach((item) => {
      if (initialized.tilts.has(item)) return;
      initialized.tilts.add(item);
      item.addEventListener('pointermove', (event) => {
        const rect = item.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        item.style.setProperty('--buffalo-tilt-x', `${(-y * 2.4).toFixed(2)}deg`);
        item.style.setProperty('--buffalo-tilt-y', `${(x * 2.4).toFixed(2)}deg`);
      });
      item.addEventListener('pointerleave', () => {
        item.style.setProperty('--buffalo-tilt-x', '0deg');
        item.style.setProperty('--buffalo-tilt-y', '0deg');
      });
    });
  };

  const initButtons = (root) => {
    root.querySelectorAll('.buffalo-button').forEach((button) => {
      if (initialized.buttons.has(button)) return;
      initialized.buttons.add(button);
      button.addEventListener('click', (event) => {
        if (reduceMotion.matches) return;
        const rect = button.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'buffalo-button__ripple';
        ripple.style.left = `${event.clientX - rect.left}px`;
        ripple.style.top = `${event.clientY - rect.top}px`;
        button.append(ripple);
        ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
      });
    });
  };

  let scrollQueued = false;
  const updateScrollDetails = () => {
    scrollQueued = false;
    document.body.classList.toggle('buffalo-scrolled', window.scrollY > 54);
    const progress = document.querySelector('[data-buffalo-scroll-progress]');
    const scrollRange = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    if (progress) progress.style.transform = `scaleX(${clamp(window.scrollY / scrollRange, 0, 1)})`;
    if (reduceMotion.matches) return;
    document.querySelectorAll('[data-buffalo-parallax]').forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      section.style.setProperty('--buffalo-parallax-y', `${clamp(-rect.top * 0.055, -28, 42)}px`);
    });
  };

  const onScroll = () => {
    if (scrollQueued) return;
    scrollQueued = true;
    window.requestAnimationFrame(updateScrollDetails);
  };

  const initSpotlights = (root) => {
    if (!finePointer.matches || reduceMotion.matches) return;
    root.querySelectorAll('[data-buffalo-spotlight]').forEach((section) => {
      if (initialized.spotlights.has(section)) return;
      initialized.spotlights.add(section);
      section.addEventListener('pointermove', (event) => {
        const rect = section.getBoundingClientRect();
        section.style.setProperty('--buffalo-spot-x', `${((event.clientX - rect.left) / rect.width * 100).toFixed(1)}%`);
        section.style.setProperty('--buffalo-spot-y', `${((event.clientY - rect.top) / rect.height * 100).toFixed(1)}%`);
      });
    });

    root.querySelectorAll('[data-buffalo-coin-scene]').forEach((scene) => {
      if (initialized.coinScenes.has(scene)) return;
      initialized.coinScenes.add(scene);
      const coin = scene.querySelector('.buffalo-coin-mark');
      scene.addEventListener('pointermove', (event) => {
        if (!coin) return;
        const rect = scene.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        coin.style.setProperty('--buffalo-coin-x', `${(-y * 7).toFixed(2)}deg`);
        coin.style.setProperty('--buffalo-coin-y', `${(x * 7).toFixed(2)}deg`);
      });
      scene.addEventListener('pointerleave', () => {
        if (!coin) return;
        coin.style.setProperty('--buffalo-coin-x', '0deg');
        coin.style.setProperty('--buffalo-coin-y', '0deg');
      });
    });
  };

  const initRewardTabs = (root) => {
    root.querySelectorAll('[data-buffalo-rewards-tabs]').forEach((tabs) => {
      if (initialized.rewardTabs.has(tabs)) return;
      initialized.rewardTabs.add(tabs);
      const controls = [...tabs.querySelectorAll('[data-buffalo-reward-tab]')];
      const panels = [...tabs.querySelectorAll('[data-buffalo-reward-panel]')];

      const activate = (control, moveFocus = false) => {
        const key = control.dataset.buffaloRewardTab;
        controls.forEach((button) => {
          const active = button === control;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => {
          const active = panel.dataset.buffaloRewardPanel === key;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        if (moveFocus) control.focus();
      };

      controls.forEach((control, index) => {
        control.addEventListener('click', () => activate(control));
        control.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = controls.length - 1;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + controls.length) % controls.length;
          else nextIndex = (index + 1) % controls.length;
          activate(controls[nextIndex], true);
        });
      });
    });
  };

  const readAddContext = (control) => {
    const formScope = control.closest('product-form-component, form');
    const displayScope = control.closest('.product-card, quick-add-component, .product-information, .shopify-section') || document;
    const quickAdd = control.closest('quick-add-component');
    const titleNode = displayScope.querySelector('[data-testid="sticky-product-title"], .view-product-title a, .product-details h1, .product-card__content h3, .product-card__content h5, product-title .title-text');
    const title = quickAdd?.dataset.productTitle || titleNode?.textContent?.trim() || document.querySelector('main h1')?.textContent?.trim() || 'Your Buffalo Series piece';

    const selectedValues = [];
    const optionScope = formScope || displayScope;
    optionScope.querySelectorAll('.variant-option input[type="radio"]:checked, .variant-option select').forEach((input) => {
      const value = input.value?.trim();
      if (value && !selectedValues.includes(value)) selectedValues.push(value.replace(/\s+-\s+Unavailable$/i, ''));
    });
    const stickyVariant = displayScope.querySelector('[data-testid="sticky-variant-title"]')?.textContent?.trim();
    if (!selectedValues.length && stickyVariant) selectedValues.push(stickyVariant);

    const imageNode = displayScope.querySelector('.buffalo-product-remaster img, [data-buffalo-render-source], .product-card__media img, .card-gallery img, [data-testid="sticky-product-image"]')
      || document.querySelector('main[data-template^="product"] [data-buffalo-render-source]');

    return {
      title,
      variant: selectedValues.length ? selectedValues.join(' / ') : 'Selected piece secured',
      image: imageNode?.currentSrc || imageNode?.src || '',
      alt: imageNode?.alt || '',
    };
  };

  const hideCartCelebration = () => {
    const celebration = document.querySelector('[data-buffalo-cart-celebration]');
    if (!celebration) return;
    window.clearTimeout(cartCelebrationTimer);
    celebration.classList.remove('is-visible');
    celebration.setAttribute('aria-hidden', 'true');
  };

  const hideClickTransition = () => {
    const transition = document.querySelector('[data-buffalo-click-transition]');
    if (!transition) return;
    window.clearTimeout(clickTransitionTimer);
    transition.classList.remove('is-visible', 'is-monogram', 'is-coin', 'is-home', 'is-collection', 'is-cart');
    transition.setAttribute('aria-hidden', 'true');
  };

  const showClickTransition = (mode = 'monogram', label = 'Entering the House', duration = 900, kicker = 'BUFFALO SERIES / PRIVATE WORLD') => {
    const transition = document.querySelector('[data-buffalo-click-transition]');
    if (!transition) return;
    const labelNode = transition.querySelector('[data-buffalo-click-label]');
    const kickerNode = transition.querySelector('[data-buffalo-click-kicker]');
    window.clearTimeout(clickTransitionTimer);
    transition.classList.remove('is-visible', 'is-monogram', 'is-coin', 'is-home', 'is-collection', 'is-cart');
    transition.classList.add(`is-${mode}`);
    transition.setAttribute('aria-hidden', 'false');
    if (labelNode) labelNode.textContent = label;
    if (kickerNode) kickerNode.textContent = kicker;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => transition.classList.add('is-visible')));
    clickTransitionTimer = window.setTimeout(hideClickTransition, duration);
  };

  const transitionForUrl = (url) => {
    const path = url.pathname.replace(/\/+$/, '') || '/';
    if (path === '/') return { mode: 'home', label: 'Returning to House 001', kicker: 'BUFFALO SERIES / HOME', delay: 720 };
    if (path === '/cart') return { mode: 'cart', label: 'Opening the House Bag', kicker: 'BUFFALO SERIES / YOUR SELECTION', delay: 620 };
    if (path === '/collections' || path.startsWith('/collections/')) {
      return { mode: 'collection', label: 'Opening the Collection', kicker: 'BUFFALO SERIES / THE ARCHIVE', delay: 700 };
    }
    return { mode: 'monogram', label: 'Entering the House', kicker: 'BUFFALO SERIES / PRIVATE WORLD', delay: 560 };
  };

  const faqAnswers = {
    sizing: 'Buffalo Series tees, sweatshirts, and hoodies are presented with an intentional oversized fit. Choose your usual size for the designed roomy silhouette, or size down once for a closer fit. Use the product size guide for exact measurements.',
    orders: 'Your confirmation email and account order history contain the latest order status. Tracking appears after the carrier receives the package. For account-specific help, contact the House with your order number and checkout email.',
    shipping: 'Available shipping methods, cost, and the current delivery estimate appear at checkout. Tracking is emailed after fulfillment and the first carrier scan.',
    coins: 'Every eligible merchandise dollar earns two separate balances after fulfillment: 1 Lumphey Coin goes into your spendable wallet and 1 XP goes toward House status. Coin merchandise is planned at 200 for the Lumphey keychain, 500 for the House hat, 1,000 for the two-shirt set, and 2,000 for the rare limited merch box. Free and Pass members pay the same Coin prices. Inventory and redemption require the loyalty backend.',
    battle: 'The XP Battle Pass has one universal track with eight permanent milestones from 0 to 1,500 XP. Every account unlocks the exact same reward at each level. XP is never spent; an active $5 Series Pass only earns XP twice as fast.',
    pass: 'The Buffalo Series Pass is $5 per month. While active, each eligible merchandise dollar earns 2 Lumphey Coins for the wallet and 2 XP toward the Battle Pass instead of 1 and 1. It does not change Coin Store prices or level rewards. A subscription service must be connected before enrollment goes live.',
    returns: 'Return eligibility follows the store return policy. When a qualifying return is completed, the matching Lumphey Coins and XP from that item are reversed so balances stay accurate.',
    discounts: 'The Series Pass is an earning-speed upgrade, not a separate discount tier. Free and Pass members see the same Coin Store prices and the same Battle Pass rewards; Pass members simply earn Coins and XP twice as fast.',
    default: 'I can help with sizing, shipping, tracking an order, returns, Lumphey Coins, XP, or the $5 Buffalo Series Pass. Try including one of those topics in your question.',
  };

  const answerFaq = (question = '') => {
    const normalized = String(question).toLowerCase();
    if (/size|fit|small|medium|large|oversized/.test(normalized)) return faqAnswers.sizing;
    if (/track|where.*order|order status|confirmation/.test(normalized)) return faqAnswers.orders;
    if (/ship|deliver|arrival|carrier/.test(normalized)) return faqAnswers.shipping;
    if (/coin|reward|wallet|redeem/.test(normalized)) return faqAnswers.coins;
    if (/battle|xp level|milestone|level reward|xp track/.test(normalized)) return faqAnswers.battle;
    if (/pass|premium|membership|monthly|2x|double/.test(normalized)) return faqAnswers.pass;
    if (/return|refund|exchange/.test(normalized)) return faqAnswers.returns;
    if (/coupon|discount|offer|promo/.test(normalized)) return faqAnswers.discounts;
    return faqAnswers.default;
  };

  const appendConciergeMessage = (text, user = false) => {
    const conversation = document.querySelector('[data-buffalo-concierge-conversation]');
    if (!conversation || !text) return;
    const message = document.createElement('p');
    if (user) message.className = 'is-user';
    const avatar = document.createElement('span');
    avatar.textContent = 'BS';
    const copy = document.createElement('em');
    copy.textContent = text;
    message.append(avatar, copy);
    conversation.append(message);
    conversation.scrollTop = conversation.scrollHeight;
  };

  const openConcierge = (topic = '') => {
    const concierge = document.querySelector('[data-buffalo-concierge]');
    if (!concierge) return;
    hideCartCelebration();
    concierge.classList.add('is-open');
    concierge.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-buffalo-concierge-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    if (topic && faqAnswers[topic]) {
      window.setTimeout(() => appendConciergeMessage(faqAnswers[topic]), reduceMotion.matches ? 0 : 180);
    }
  };

  const closeConcierge = () => {
    const concierge = document.querySelector('[data-buffalo-concierge]');
    concierge?.classList.remove('is-open');
    concierge?.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-buffalo-concierge-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  };

  const showCartCelebration = (context = lastAddContext) => {
    const celebration = document.querySelector('[data-buffalo-cart-celebration]');
    if (!celebration) return;
    const title = celebration.querySelector('[data-buffalo-cart-product-title]');
    const variant = celebration.querySelector('[data-buffalo-cart-product-variant]');
    const image = celebration.querySelector('[data-buffalo-cart-product-image]');
    if (context?.title && title) title.textContent = context.title;
    if (variant) variant.textContent = context?.variant || 'Selected piece secured';
    if (context?.image && image) {
      image.src = context.image;
      image.alt = context.alt || '';
    }
    window.clearTimeout(cartCelebrationTimer);
    celebration.classList.remove('is-visible');
    celebration.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => celebration.classList.add('is-visible')));
    const cartIcon = document.querySelector('.header-actions__cart-icon, [data-testid="cart-icon"]');
    cartIcon?.classList.remove('buffalo-cart-pulse');
    window.requestAnimationFrame(() => cartIcon?.classList.add('buffalo-cart-pulse'));
    cartCelebrationTimer = window.setTimeout(() => {
      celebration.classList.remove('is-visible');
      celebration.setAttribute('aria-hidden', 'true');
      cartIcon?.classList.remove('buffalo-cart-pulse');
    }, 6000);
  };

  const initHouseExperience = () => {
    if (houseExperienceReady) return;
    houseExperienceReady = true;
    const rewardsDialog = document.querySelector('[data-buffalo-rewards-dialog]');
    const checkoutTransition = document.querySelector('[data-buffalo-checkout-transition]');
    const menuDetails = document.querySelector('#Details-menu-drawer-container');
    const syncMenuState = () => document.body.classList.toggle('buffalo-menu-open', Boolean(menuDetails?.hasAttribute('open')));
    menuDetails?.addEventListener('toggle', syncMenuState);
    if (menuDetails) new MutationObserver(syncMenuState).observe(menuDetails, { attributes: true, attributeFilter: ['open'] });
    syncMenuState();

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const addControl = target.closest('.add-to-cart-button');
      if (addControl && !addControl.disabled) lastAddContext = readAddContext(addControl);

      if (target.closest('[data-buffalo-cart-close]')) {
        hideCartCelebration();
        return;
      }

      if (target.closest('[data-testid="cart-drawer-trigger"]')) {
        closeConcierge();
        showClickTransition('cart', 'Opening the House Bag', reduceMotion.matches ? 80 : 780, 'BUFFALO SERIES / YOUR SELECTION');
        return;
      }

      const conciergeOpen = target.closest('[data-buffalo-concierge-open]');
      if (conciergeOpen) {
        event.preventDefault();
        openConcierge(conciergeOpen.dataset.buffaloConciergeTopic || '');
        return;
      }

      if (target.closest('[data-buffalo-concierge-close]')) {
        closeConcierge();
        return;
      }

      const faqControl = target.closest('[data-buffalo-faq-question]');
      if (faqControl) {
        const topic = faqControl.dataset.buffaloFaqQuestion || '';
        appendConciergeMessage(faqControl.textContent.trim(), true);
        window.setTimeout(() => appendConciergeMessage(faqAnswers[topic] || answerFaq(topic)), reduceMotion.matches ? 0 : 220);
        return;
      }

      if (target.closest('[data-buffalo-rewards-open]')) {
        event.preventDefault();
        closeConcierge();
        showClickTransition('coin', 'Opening the Lumphey Vault', reduceMotion.matches ? 80 : 760, 'LUMPHEY COINS / MEMBER VAULT');
        window.setTimeout(() => {
          hideClickTransition();
          if (rewardsDialog instanceof HTMLDialogElement) {
            if (!rewardsDialog.open) rewardsDialog.showModal();
          } else {
            rewardsDialog?.setAttribute('open', '');
          }
          document.body.classList.add('buffalo-rewards-open');
        }, reduceMotion.matches ? 0 : 620);
        return;
      }

      if (target.closest('[data-buffalo-rewards-close]') || target === rewardsDialog) {
        if (rewardsDialog instanceof HTMLDialogElement) rewardsDialog.close();
        else rewardsDialog?.removeAttribute('open');
        document.body.classList.remove('buffalo-rewards-open');
        return;
      }

      const checkoutControl = target.closest('[name="checkout"], a[href*="/checkout"], button[data-checkout]');
      if (checkoutControl && checkoutTransition) {
        window.clearTimeout(checkoutTransitionTimer);
        checkoutTransition.classList.remove('is-visible');
        checkoutTransition.setAttribute('aria-hidden', 'false');
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => checkoutTransition.classList.add('is-visible')));
        checkoutTransitionTimer = window.setTimeout(() => {
          checkoutTransition.classList.remove('is-visible');
          checkoutTransition.setAttribute('aria-hidden', 'true');
        }, 6000);
        return;
      }

      const link = target.closest('a[href]');
      if (!link || link.target || link.hasAttribute('download') || link.hasAttribute('data-buffalo-no-transition')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || navigationPending) return;
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      try {
        const url = new URL(link.href, window.location.href);
        if (url.origin === window.location.origin && url.href !== window.location.href) {
          event.preventDefault();
          navigationPending = true;
          document.body.classList.add('buffalo-navigating');
          closeConcierge();
          const scene = transitionForUrl(url);
          showClickTransition(scene.mode, scene.label, 2400, scene.kicker);
          window.setTimeout(() => window.location.assign(url.href), reduceMotion.matches ? 0 : scene.delay);
        }
      } catch (_) {
        // Ignore malformed or app-owned URLs.
      }
    });

    rewardsDialog?.addEventListener('close', () => document.body.classList.remove('buffalo-rewards-open'));
    document.querySelector('[data-buffalo-concierge-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = event.currentTarget.querySelector('input[name="question"]');
      const question = input?.value.trim() || '';
      if (!question) return;
      appendConciergeMessage(question, true);
      if (input) input.value = '';
      window.setTimeout(() => appendConciergeMessage(answerFaq(question)), reduceMotion.matches ? 0 : 260);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeConcierge();
    });
    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source !== 'product-form-component' || event.detail?.data?.didError) return;
      showCartCelebration();
    });
    document.addEventListener('variant:update', () => window.setTimeout(() => init(document), 0));
    window.addEventListener('pageshow', () => {
      document.body.classList.remove('buffalo-navigating');
      navigationPending = false;
      hideClickTransition();
      syncMenuState();
      checkoutTransition?.classList.remove('is-visible');
      checkoutTransition?.setAttribute('aria-hidden', 'true');
    });
  };

  const init = (root = document) => {
    initReveals(root);
    root.querySelectorAll('[data-buffalo-color-gallery]').forEach(initColorGallery);
    root.querySelectorAll('[data-buffalo-color-render]').forEach((render) => {
      if (!render.closest('[data-buffalo-color-gallery]')) scheduleRender(render, true);
    });
    initTilt(root);
    initButtons(root);
    initSpotlights(root);
    initRewardTabs(root);
    initLuxuryRails(root);
    initHouseExperience();
    updateScrollDetails();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  init();
})();
